import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";
import {
    Flame,
    Trophy,
    Target,
    Clock,
    Play,
    ArrowUpRight,
    Sparkles,
    Zap,
    BookOpen,
    Award,
    Crown,
    Timer,
} from "lucide-react";

import Layout from "../components/Layout";
import ProgressRing from "../components/ProgressRing";
import BadgeIcon from "../components/BadgeIcon";
import MyAssociationsCard from "../components/MyAssociationsCard";
import NewsTerrainCard from "../components/NewsTerrainCard";
import AssoStoriesCarousel from "../components/AssoStoriesCarousel";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

// Iter-27.33 — French level labels (used in hero "Reprendre la formation").
const LEVEL_FR = {
    beginner: "Débutant",
    intermediate: "Intermédiaire",
    advanced: "Avancé",
    expert: "Expert",
};

const QUOTES = [
    "Chaque \"non\" te rapproche d'un \"oui\".",
    "L'énergie se gère, elle ne se subit pas.",
    "10 secondes pour changer une vie.",
];

function useCountdown(deadlineIso) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    if (!deadlineIso) return null;
    const ms = Math.max(0, new Date(deadlineIso).getTime() - now);
    return {
        d: Math.floor(ms / 86400000),
        h: Math.floor((ms % 86400000) / 3600000),
        m: Math.floor((ms % 3600000) / 60000),
    };
}

export default function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [modules, setModules] = useState([]);
    const [leaders, setLeaders] = useState([]);
    const [allBadges, setAllBadges] = useState([]);
    const [weekly, setWeekly] = useState(null);
    const quote = QUOTES[Math.floor(Math.random() * QUOTES.length)];

    useEffect(() => {
        (async () => {
            try {
                const [s, m, l, w] = await Promise.all([
                    api.get("/me/stats"),
                    api.get("/modules"),
                    api.get("/leaderboard"),
                    api.get("/leaderboard/weekly"),
                ]);
                setStats(s.data);
                setModules(m.data);
                setLeaders(l.data.slice(0, 4));
                setAllBadges(s.data.badges || []);
                setWeekly(w.data);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const inProgress = modules.filter(
        (m) => m.progress_percent > 0 && !m.completed,
    );
    const next = inProgress[0] || modules.find((m) => !m.completed) || modules[0];
    const activity = stats?.activity_7d || [];
    const todayIdx = activity.length - 1;
    const countdown = useCountdown(weekly?.deadline);
    const myWeekly = weekly?.leaders?.find((l) => l.user_id === user?.user_id);
    // Iter 30.49 — weeklyTop1 ne doit JAMAIS être quelqu'un à 0 pt (sinon le
    // dashboard affiche un faux "Top hebdo" basé sur l'ordre alphabétique).
    const weeklyTop1 = weekly?.leaders?.find((l) => (l.weekly_points || 0) > 0);
    const myWeeklyScored = (myWeekly?.weekly_points || 0) > 0;

    return (
        <Layout
            title={`Salut, ${user?.name?.split(" ")[0] || "🔥"}`}
            subtitle={quote}
        >
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
                {/* Iter-27.69 — Stories des associations en haut du dashboard */}
                <AssoStoriesCarousel />

                {/* Big progress card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="col-span-12 lg:col-span-5 rounded-3xl p-6 lg:p-8 relative overflow-hidden bg-shariti-orange text-white"
                    data-testid="dashboard-progress-card"
                >
                    <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-shariti-yellow/30 blur-3xl" />
                    <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-6">
                            <Sparkles className="w-4 h-4" />
                            <p className="text-xs font-thin tracking-[0.3em] uppercase">
                                Progression globale
                            </p>
                        </div>
                        <div className="flex items-center gap-6 lg:gap-8">
                            <ProgressRing
                                value={stats?.overall_percent || 0}
                                size={140}
                                stroke={12}
                                color="#FFFFFF"
                                trackColor="rgba(255,255,255,0.15)"
                            >
                                <span className="text-4xl font-extrabold">
                                    {stats?.overall_percent || 0}
                                    <span className="text-lg">%</span>
                                </span>
                                <span className="text-[10px] font-thin tracking-[0.25em] uppercase mt-1">
                                    Maîtrisé
                                </span>
                            </ProgressRing>
                            <div className="flex-1">
                                <p className="text-5xl lg:text-6xl font-extrabold leading-none mb-3">
                                    {stats?.completed_modules || 0}
                                </p>
                                <p className="text-sm font-medium opacity-90 mb-4">
                                    modules complétés sur {stats?.total_modules || 0}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <div className="bg-shariti-black/30 backdrop-blur rounded-xl px-3 py-1.5 flex items-center gap-1.5">
                                        <Flame className="w-3.5 h-3.5" />
                                        <span className="text-xs font-bold">
                                            {user?.streak_days || 0}j de série
                                        </span>
                                    </div>
                                    <Link
                                        to="/historique"
                                        data-testid="dashboard-points-pill"
                                        title="Voir l'historique de mes points"
                                        className="bg-shariti-black/30 backdrop-blur rounded-xl px-3 py-1.5 flex items-center gap-1.5 hover:bg-shariti-black/50 transition-all group"
                                    >
                                        <Trophy className="w-3.5 h-3.5" />
                                        <span className="text-xs font-bold">
                                            {stats?.points || 0} pts
                                        </span>
                                        <ArrowUpRight className="w-3 h-3 opacity-70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Score moyen */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.05 }}
                    className="col-span-6 lg:col-span-3 glass-card glass-card-hover rounded-3xl p-5 lg:p-6 transition-all"
                    data-testid="dashboard-score-card"
                >
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50">
                            Score moyen
                        </p>
                        <Target className="w-5 h-5 text-shariti-orange" />
                    </div>
                    <p className="text-5xl lg:text-6xl font-extrabold text-white leading-none">
                        {stats?.avg_score || 0}
                        <span className="text-2xl text-white/40">%</span>
                    </p>
                    <div className="mt-4 flex items-center gap-1 text-xs font-bold text-shariti-orange-light">
                        <ArrowUpRight className="w-3.5 h-3.5" />
                        <span>aux quiz</span>
                    </div>
                </motion.div>

                {/* Temps */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="col-span-6 lg:col-span-2 glass-card glass-card-hover rounded-3xl p-5 lg:p-6"
                    data-testid="dashboard-time-card"
                >
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50">
                            Temps
                        </p>
                        <Clock className="w-5 h-5 text-shariti-yellow" />
                    </div>
                    <p className="text-4xl lg:text-5xl font-extrabold text-white leading-none">
                        {Math.floor((stats?.time_minutes || 0) / 60)}
                        <span className="text-xl text-white/40">h</span>
                        {(stats?.time_minutes || 0) % 60}
                    </p>
                    <p className="text-xs text-white/40 mt-3">de formation</p>
                </motion.div>

                {/* Rang */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                    className="col-span-12 lg:col-span-2 glass-card glass-card-hover rounded-3xl p-5 lg:p-6"
                    data-testid="dashboard-rank-card"
                >
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50">
                            Mon rang
                        </p>
                        <Trophy className="w-5 h-5 text-shariti-orange" />
                    </div>
                    <p className="text-5xl lg:text-6xl font-extrabold text-white leading-none flex items-baseline gap-1">
                        <span className="text-shariti-orange">#</span>
                        {stats?.rank || "-"}
                    </p>
                    <Link
                        to="/leaderboard"
                        className="text-xs text-shariti-orange-light hover:text-shariti-orange font-bold mt-3 inline-flex items-center gap-1"
                        data-testid="dashboard-rank-link"
                    >
                        voir le classement <ArrowUpRight className="w-3 h-3" />
                    </Link>
                </motion.div>

                {/* Iter-27.21 — Mes associations terrain (visible en haut, hyper saillant) */}
                <div className="col-span-12">
                    <MyAssociationsCard variant="full" />
                </div>

                {/* Iter-27.35 — Actualités terrain des associations affectées */}
                <div className="col-span-12">
                    <NewsTerrainCard />
                </div>

                {/* Continue learning */}
                {next && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="col-span-12 lg:col-span-7 rounded-3xl p-6 lg:p-8 glass-card glass-card-hover relative overflow-hidden"
                        data-testid="dashboard-continue-card"
                    >
                        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-center">
                            <div className="min-w-0">
                                <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-3">
                                    Reprendre la formation
                                </p>
                                <h3 className="text-2xl sm:text-3xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-extrabold text-white tracking-tight mb-2 break-words leading-[1.1]">
                                    {next.title}
                                </h3>
                                <p className="text-white/60 text-sm font-light mb-5 line-clamp-2">
                                    {next.description}
                                </p>
                                <div className="flex items-center gap-2 mb-5">
                                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${next.progress_percent}%` }}
                                            transition={{ duration: 1, delay: 0.4 }}
                                            className="h-full bg-shariti-orange rounded-full"
                                        />
                                    </div>
                                    <span className="text-sm font-bold text-white">
                                        {next.progress_percent}%
                                    </span>
                                </div>
                                <Link
                                    to={`/module/${next.module_id}`}
                                    data-testid="continue-module-button"
                                    className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-6 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                >
                                    <Play className="w-4 h-4 fill-white" />
                                    Lancer mon entraînement
                                </Link>
                            </div>
                            {next.cover_image && (
                                <div className="relative aspect-video rounded-2xl overflow-hidden">
                                    <img
                                        src={next.cover_image}
                                        alt={next.title}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-shariti-black via-transparent to-shariti-orange/20" />
                                    <div className="absolute bottom-3 left-3 flex items-center gap-2">
                                        <span className="text-xs font-bold text-white bg-shariti-black/60 backdrop-blur px-2.5 py-1 rounded-full uppercase tracking-widest">
                                            {LEVEL_FR[next.level] || next.level}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* Activity chart */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.25 }}
                    className="col-span-12 lg:col-span-5 glass-card glass-card-hover rounded-3xl p-6 lg:p-7"
                    data-testid="dashboard-activity-card"
                >
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                                Activité 7 jours
                            </p>
                            <p className="text-2xl font-extrabold text-white">
                                {activity.reduce((s, d) => s + d.value, 0)}{" "}
                                <span className="text-sm font-light text-white/50">
                                    quiz
                                </span>
                            </p>
                        </div>
                        <div className="bg-shariti-orange/15 rounded-xl px-3 py-1.5">
                            <span className="text-xs font-bold text-shariti-orange-light">
                                Cette semaine
                            </span>
                        </div>
                    </div>
                    <div className="h-44 -mx-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={activity}>
                                <XAxis
                                    dataKey="day"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: "#888", fontSize: 11, fontFamily: "Montserrat" }}
                                />
                                <Tooltip
                                    cursor={{ fill: "rgba(238,114,46,0.05)" }}
                                    contentStyle={{
                                        background: "#181818",
                                        border: "1px solid #EE722E",
                                        borderRadius: 12,
                                        fontFamily: "Montserrat",
                                    }}
                                    labelStyle={{ color: "#EE722E", fontWeight: 800, marginBottom: 4 }}
                                    itemStyle={{ color: "#fff", fontWeight: 700 }}
                                    formatter={(value) => [`${value} quiz`, null]}
                                    separator=""
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]}>
                                    {activity.map((_, i) => (
                                        <Cell
                                            key={i}
                                            fill={i === todayIdx ? "#EE722E" : "#3a2a1f"}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>

                {/* Badges */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="col-span-12 lg:col-span-6 glass-card glass-card-hover rounded-3xl p-6 lg:p-7"
                    data-testid="dashboard-badges-card"
                >
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                                Tes badges
                            </p>
                            <h3 className="text-xl font-extrabold text-white">
                                {allBadges.length} obtenu{allBadges.length > 1 ? "s" : ""}
                            </h3>
                        </div>
                        <Award className="w-6 h-6 text-shariti-yellow" />
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {allBadges.length === 0 ? (
                            <p className="text-sm text-white/40">
                                Termine ton premier module pour décrocher ton premier badge ⭐
                            </p>
                        ) : (
                            allBadges.map((b) => (
                                <div key={b.badge_id} className="text-center">
                                    <BadgeIcon
                                        badge={{
                                            badge_id: b.badge_id,
                                            icon: b.icon || "trophy",
                                            color: b.color || "#EE722E",
                                            name: b.name,
                                        }}
                                        size="md"
                                    />
                                    <p className="text-[10px] font-bold text-white/70 mt-1.5 max-w-[60px] truncate">
                                        {b.name}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </motion.div>

                {/* Quick access terrain */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.35 }}
                    className="col-span-12 lg:col-span-6 rounded-3xl p-6 lg:p-7 relative overflow-hidden"
                    data-testid="dashboard-terrain-card"
                    style={{
                        background:
                            "linear-gradient(135deg, #181818 0%, #2a1a0d 100%)",
                        border: "1px solid rgba(238,114,46,0.3)",
                    }}
                >
                    <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-shariti-orange/20 blur-3xl" />
                    <div className="relative z-10 flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-shariti-orange flex items-center justify-center orange-glow shrink-0">
                            <Zap className="w-7 h-7 text-white" fill="white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-1">
                                Mode terrain rapide
                            </p>
                            <h3 className="text-xl lg:text-2xl font-extrabold text-white tracking-tight">
                                Accès express aux pitches & objections
                            </h3>
                        </div>
                        <Link
                            to="/terrain"
                            data-testid="dashboard-terrain-link"
                            className="hidden sm:inline-flex items-center gap-2 bg-shariti-orange text-white font-bold py-2.5 px-4 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                        >
                            Ouvrir
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <Link
                        to="/terrain"
                        className="sm:hidden mt-4 inline-flex w-full items-center justify-center gap-2 bg-shariti-orange text-white font-bold py-2.5 rounded-2xl orange-glow"
                    >
                        Ouvrir le mode terrain <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </motion.div>

                {/* Weekly Challenge */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.38 }}
                    className="col-span-12 rounded-3xl p-6 lg:p-7 relative overflow-hidden bg-gradient-to-br from-shariti-orange to-shariti-orange-light text-white"
                    data-testid="dashboard-weekly-card"
                >
                    <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-shariti-yellow/30 blur-3xl" />
                    <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
                    <div className="relative z-10 grid lg:grid-cols-4 gap-4 lg:gap-6 items-center">
                        <div className="lg:col-span-2">
                            <div className="flex items-center gap-2 mb-2">
                                <Crown className="w-5 h-5" />
                                <p className="text-xs font-thin tracking-[0.3em] uppercase opacity-90">
                                    Challenge de la semaine
                                </p>
                            </div>
                            <h3 className="text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight mb-1">
                                Top 1 = 200 pts bonus + badge
                            </h3>
                            <p className="text-sm font-light opacity-90">
                                Le classement se remet à zéro chaque lundi
                            </p>
                        </div>

                        {countdown && (
                            <div
                                className="bg-shariti-black/30 backdrop-blur rounded-2xl p-4"
                                data-testid="dashboard-weekly-countdown"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Timer className="w-4 h-4" />
                                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase opacity-90">
                                        Fin du défi dans
                                    </p>
                                </div>
                                <div className="flex items-baseline gap-1.5">
                                    <span className="text-2xl lg:text-3xl font-extrabold leading-none tabular-nums">
                                        {countdown.d}j
                                    </span>
                                    <span className="text-xl lg:text-2xl font-extrabold tabular-nums">
                                        {String(countdown.h).padStart(2, "0")}h
                                    </span>
                                    <span className="text-base font-bold tabular-nums opacity-80">
                                        {String(countdown.m).padStart(2, "0")}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="bg-shariti-black/30 backdrop-blur rounded-2xl p-4">
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase opacity-90 mb-2">
                                {myWeeklyScored && myWeekly?.rank === 1
                                    ? "Tu es en tête"
                                    : "Top hebdo"}
                            </p>
                            {weeklyTop1 ? (
                                <div className="flex items-center gap-2">
                                    {weeklyTop1.avatar ? (
                                        <img
                                            src={weeklyTop1.avatar}
                                            alt={weeklyTop1.name}
                                            className="w-9 h-9 rounded-xl object-cover"
                                        />
                                    ) : (
                                        <div className="w-9 h-9 rounded-xl bg-shariti-yellow flex items-center justify-center font-extrabold text-shariti-black text-sm">
                                            {weeklyTop1.name?.[0]}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="font-extrabold text-sm truncate">
                                            {weeklyTop1.name}
                                        </p>
                                        <p className="text-xs font-bold opacity-90">
                                            {weeklyTop1.weekly_points} pts cette semaine
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs opacity-80">Premier arrivé !</p>
                            )}
                            {myWeeklyScored && myWeekly.rank > 1 && (
                                <p className="text-[10px] mt-2 opacity-80" data-testid="dashboard-weekly-myrank">
                                    Toi : #{myWeekly.rank} · {myWeekly.weekly_points} pts hebdo
                                </p>
                            )}
                        </div>
                    </div>
                    <Link
                        to="/leaderboard"
                        data-testid="dashboard-weekly-link"
                        className="mt-5 inline-flex items-center gap-2 bg-shariti-black text-white font-extrabold py-2.5 px-5 rounded-2xl hover:bg-shariti-black/80 transition-all"
                    >
                        Voir le classement hebdo
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </motion.div>

                {/* Leaderboard preview */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="col-span-12 glass-card glass-card-hover rounded-3xl p-6 lg:p-7"
                    data-testid="dashboard-leaderboard-card"
                >
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                                Top performers
                            </p>
                            <h3 className="text-xl font-extrabold text-white">Leaderboard</h3>
                        </div>
                        <Link
                            to="/leaderboard"
                            className="text-xs font-bold text-shariti-orange-light hover:text-shariti-orange flex items-center gap-1"
                        >
                            tout voir <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <div className="space-y-2">
                        {leaders.map((l, i) => (
                            <div
                                key={l.user_id}
                                className={`flex items-center gap-3 p-3 rounded-2xl ${
                                    i === 0 ? "bg-shariti-orange/10" : "bg-white/3"
                                }`}
                            >
                                <span
                                    className={`w-8 h-8 rounded-xl flex items-center justify-center font-extrabold text-sm ${
                                        i === 0
                                            ? "bg-shariti-orange text-white"
                                            : i === 1
                                            ? "bg-shariti-yellow text-shariti-black"
                                            : i === 2
                                            ? "bg-shariti-orange-light text-shariti-black"
                                            : "bg-white/5 text-white/60"
                                    }`}
                                >
                                    {i + 1}
                                </span>
                                {l.avatar ? (
                                    <img
                                        src={l.avatar}
                                        className="w-9 h-9 rounded-xl object-cover"
                                        alt={l.name}
                                    />
                                ) : (
                                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-shariti-orange to-shariti-yellow flex items-center justify-center font-bold text-shariti-black text-sm">
                                        {l.name?.[0]}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-white truncate text-sm">
                                        {l.name}
                                    </p>
                                    <p className="text-xs text-white/40 capitalize">
                                        {l.role} · {l.completed_modules} modules
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-extrabold text-shariti-orange">
                                        {l.points}
                                    </p>
                                    <p className="text-[10px] font-thin tracking-widest text-white/40 uppercase">
                                        pts total
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </Layout>
    );
}
